import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import ConsultationChat from "../components/ConsultationChat/ConsultationChat";
import { EmptyState, PageHeader, ToneBadge } from "../petaarUi";

function getBookingIdFromHash() {
  if (typeof window === "undefined") return "";
  const match = String(window.location.hash || "").match(/^#\/consultation\/([^/?#]+)\/?$/i);
  if (!match) return "";
  return decodeURIComponent(match[1] || "").trim();
}

export default function ConsultationPage({
  bookingId,
  user,
  userRole,
  onBack,
}) {
  const [chatStatus, setChatStatus] = useState("");

  const effectiveBookingId = useMemo(
    () => String(bookingId || getBookingIdFromHash()).trim(),
    [bookingId],
  );

  const normalizedUserRole = String(userRole || "").trim().toLowerCase();
  const chatRole = normalizedUserRole === "provider" || normalizedUserRole === "vet" ? "vet" : "owner";
  const handleBack = useMemo(() => {
    if (typeof onBack === "function") return onBack;
    return () => {
      window.location.hash = chatRole === "vet" ? "#/page/provider-bookings" : "#/page/owner-bookings";
    };
  }, [chatRole, onBack]);

  useEffect(() => {
    if (!effectiveBookingId) {
      setChatStatus("");
      return undefined;
    }
    const unsub = onSnapshot(
      doc(db, "consultationChats", effectiveBookingId),
      (snap) => {
        const data = snap.data() || {};
        setChatStatus(String(data.status || "").toLowerCase());
      },
      () => {
        setChatStatus("");
      },
    );
    return () => unsub();
  }, [effectiveBookingId]);

  if (!effectiveBookingId) {
    return (
      <div className="petaar-page">
        <EmptyState
          icon="🔗"
          title="Invalid consultation link"
          description="The consultation booking ID is missing or malformed. Return to bookings and reopen the chat from there."
          actionLabel="Back"
          onAction={handleBack}
        />
      </div>
    );
  }

  if (chatStatus === "closed") {
    return (
      <div className="petaar-page">
        <EmptyState
          icon="🧾"
          title="This consultation has ended"
          description="View the prescription and history from your bookings dashboard."
          actionLabel="Open Bookings"
          onAction={() => {
            window.location.hash = chatRole === "vet" ? "#/page/provider-bookings" : "#/page/owner-bookings";
          }}
        />
      </div>
    );
  }

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Consultation"
        title="In-App Consultation Chat"
        subtitle="Continue the consultation inside Petaar. Personal contact details remain blocked and all chat history stays tied to the booking."
        actions={(
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <ToneBadge tone={chatRole === "vet" ? "accent" : "success"}>{chatRole}</ToneBadge>
            <button type="button" className="petaar-button petaar-button--secondary" onClick={handleBack}>Back</button>
          </div>
        )}
      />
      <ConsultationChat
        bookingId={effectiveBookingId}
        userRole={chatRole}
        currentUserUid={String(user?.uid || "")}
      />
    </div>
  );
}
